import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  ButtonBase,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Popover
} from '@material-ui/core';
import React, { useRef, useState } from 'react';
import { pluralize } from '../../services/pluralize';
import { SelectorChip } from '../SelectorChip';

export type MultiSelectorOption<Key> = {
  label: React.ReactNode;
  value: Key;
};

type Props<Key> = {
  value: Set<Key>;
  onChange: (nextValue: Set<Key>) => void;
  options: MultiSelectorOption<Key>[];
  children: React.ReactNode;
  legend?: React.ReactNode;
  minMenuWidth?: string | number;
  allOption?: React.ReactNode;
  allowFocusing?: boolean;
};

const Label = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FocusButton = styled(Button)`
  margin-left: ${(p) => p.theme.spacing()}px !important;
`;

export const getAppliedLabel = (
  name: string,
  labels: string[],
  prefix = 'Filtered by'
) => {
  const ls =
    labels.length === 1 ? labels[0] : pluralize(name, labels.length, true);
  return `${prefix} ${ls}`;
};

export const MultiSelectorChip = SelectorChip;

export const MultiSelector = <Key extends string>({
  value,
  onChange,
  options,
  children,
  legend,
  minMenuWidth = 150,
  allOption,
  allowFocusing
}: Props<Key>) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const toggle = (key: Key) => {
    const nextValue = new Set(value);
    if (nextValue.has(key)) {
      nextValue.delete(key);
    } else {
      nextValue.add(key);
    }
    onChange(nextValue);
  };
  const allSelected = value.size === options.length;
  return (
    <>
      <ButtonBase onClick={() => setOpen((x) => !x)} ref={ref}>
        {children}
      </ButtonBase>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={ref.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <FormControl
          component="fieldset"
          className={css({
            margin: theme.spacing(2),
            minWidth: minMenuWidth
          })}
        >
          {legend && (
            <FormLabel
              classes={{
                root: css({
                  padding: theme.spacing(1)
                })
              }}
              component="legend"
            >
              {legend}
            </FormLabel>
          )}
          <FormGroup>
            {allOption && (
              <FormControlLabel
                classes={{
                  root: css({
                    marginLeft: 0,
                    marginRight: 0
                  })
                }}
                control={
                  <Checkbox
                    checked={allSelected}
                    color="primary"
                    indeterminate={value.size > 0 && !allSelected}
                    value={'____ALL____'}
                    onChange={() =>
                      allSelected
                        ? onChange(new Set())
                        : onChange(new Set(options.map((o) => o.value)))
                    }
                  />
                }
                label={allOption}
              />
            )}
            {options.map((o) => (
              <FormControlLabel
                key={o.value}
                control={
                  <Checkbox
                    checked={value.has(o.value)}
                    color="primary"
                    value={o.value}
                    onChange={() => toggle(o.value)}
                  />
                }
                classes={{
                  label: css({ width: '100%' }),
                  root: css({
                    marginLeft: 0,
                    marginRight: 0
                  })
                }}
                label={
                  <Label>
                    <div>{o.label}</div>
                    {allowFocusing && (
                      <FocusButton
                        size="small"
                        variant="text"
                        color="primary"
                        onClick={() => onChange(new Set([o.value]))}
                      >
                        Only
                      </FocusButton>
                    )}
                  </Label>
                }
              />
            ))}
          </FormGroup>
        </FormControl>
      </Popover>
    </>
  );
};
